// ScorePanel.js — Panel skor kesiapan Zona KHAS
import React from 'react'
import { scoreOf, KRITERIA } from './khasConstants'

function ringColor(pct) {
  if (pct >= 80) return 'var(--khas-green)'
  if (pct >= 55) return 'var(--khas-amber)'
  return 'var(--khas-red)'
}

function scoreLabel(pct, tenantOk, halalOk, sehatOk) {
  // Kalau syarat utama belum terpenuhi → belum memenuhi syarat
  if (!tenantOk || !halalOk || !sehatOk) {
    return { text: 'Belum memenuhi syarat', color: 'var(--khas-red)' }
  }
  // Syarat utama sudah terpenuhi
  if (pct >= 80) return { text: 'Siap diajukan',   color: 'var(--khas-green-d)' }
  return              { text: 'Perlu pembenahan',   color: 'var(--khas-amber)'   }
}

function CheckRow({ label, value, threshold, unit = '%', met }) {
  const ok = met !== undefined ? !!met : (value >= threshold)
  return (
    <div className="khas-check-item">
      <span className={'khas-ico ' + (ok ? 'y' : 'n')}>{ok ? '✓' : '✕'}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:13, fontWeight:500 }}>{label}</div>
        <div style={{ fontSize:12, color:'var(--khas-muted)', marginTop:1 }}>
          {value}{unit}
          <span style={{ marginLeft:4, opacity:.6 }}>
            (min. {threshold}{unit})
          </span>
        </div>
      </div>
    </div>
  )
}

function ScorePanel({ formData: d }) {
  const result = scoreOf(d)
  const { pct, minT, halalPct, sehatPct, tenantOk, halalOk, sehatOk, config  } = result

  const halalActual = Math.round(halalPct * 100)
  const sehatActual = Math.round(sehatPct * 100)
  const label       = scoreLabel(pct, tenantOk, halalOk, sehatOk)
  const ringBg      = `conic-gradient(${ringColor(pct)} ${pct}%, #eef3f0 0)`

  // Kumpulkan semua alasan yang belum terpenuhi
  const blockReasons = []
  if (!tenantOk) blockReasons.push(`Jumlah tenant belum memenuhi minimum (${minT} tenant)`)
  if (!halalOk)  blockReasons.push(`Sertifikasi halal belum memenuhi threshold (${config?.thresholdHalal}%)`)
  if (!sehatOk)  blockReasons.push(`Label sehat belum memenuhi threshold (${config?.thresholdSehat}%)`)

  return (
    <div className="khas-card">
      <h2 style={{ fontSize:16, marginBottom:4 }}>Skor Kesiapan</h2>
      <p className="khas-sub">Terhitung otomatis dari isian form</p>

      {/* Banner block — muncul kalau syarat utama belum terpenuhi */}
      {blockReasons.length > 0 && (
        <div style={{
          background:'#fef2f2', border:'1px solid #fecaca',
          borderRadius:8, padding:'10px 14px', marginBottom:14,
        }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--khas-red)', marginBottom:6 }}>
            ⚠ Belum bisa diajukan:
          </div>
          {blockReasons.map((r, i) => (
            <div key={i} style={{ fontSize:12, color:'var(--khas-red)', lineHeight:1.5 }}>
              • {r}
            </div>
          ))}
        </div>
      )}

      {/* Banner info — syarat utama terpenuhi tapi skor < 80 */}
      {blockReasons.length === 0 && pct < 80 && (
        <div style={{
          background:'#fffbeb', border:'1px solid #fde68a',
          borderRadius:8, padding:'10px 14px', marginBottom:14,
        }}>
          <div style={{ fontSize:12, color:'#92400e', lineHeight:1.5 }}>
            ⚡ Syarat utama sudah terpenuhi. Lengkapi kriteria tambahan untuk meningkatkan skor.
          </div>
        </div>
      )}

      {/* Ring gauge */}
      <div className="khas-gauge">
        <div className="khas-ring" style={{ background:ringBg }}>
          <div className="khas-ring-inner">
            <div>
              <div className="khas-ring-pct">{pct}%</div>
              <div className="khas-ring-label">kesiapan</div>
            </div>
          </div>
        </div>
        <div style={{ fontSize:13.5, fontWeight:700, color:label.color }}>
          {label.text}
        </div>
        <div style={{ fontSize:12, color:'var(--khas-muted)', marginTop:4 }}>
          {config?.label || 'Tipe A Tertutup'}
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop:'1px dashed var(--khas-border)', margin:'16px 0 12px' }} />

      {/* Persyaratan utama */}
      <div style={{ marginBottom:12 }}>
        <div style={{ fontSize:11, fontWeight:600, color:'var(--khas-muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>
          Persyaratan utama
        </div>
        <CheckRow
          label="Jumlah tenant"
          value={d.tenant}
          threshold={minT}
          unit=""
          met={tenantOk}
        />
        <CheckRow
          label="Sertifikasi Halal"
          value={halalActual}
          threshold={config?.thresholdHalal ?? 75}
        />
        <CheckRow
          label="SLHS / IKL (Aman & Sehat)"
          value={sehatActual}
          threshold={config?.thresholdSehat ?? 90}
        />
      </div>

      {/* Divider */}
      <div style={{ borderTop:'1px dashed var(--khas-border)', margin:'12px 0 12px' }} />

      {/* 6 Kriteria — informatif */}
      <div>
        <div style={{ fontSize:11, fontWeight:600, color:'var(--khas-muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:4 }}>
          6 Kriteria Zona KHAS
        </div>
        <div style={{ fontSize:11, color:'var(--khas-muted)', marginBottom:8 }}>
          Opsional — tidak mempengaruhi kelayakan submit
        </div>
        {KRITERIA.map((c, i) => {
          const checked = !!d['k'+i]
          return (
            <div key={i} className="khas-check-item">
              <span className={'khas-ico ' + (checked ? 'y' : 'n')}>
                {checked ? '✓' : '✕'}
              </span>
              <div style={{ fontSize:12.5, fontWeight: checked ? 600 : 400 }}>
                {c.t}
              </div>
            </div>
          )
        })}
      </div>

      {/* Divider */}
      <div style={{ borderTop:'1px dashed var(--khas-border)', margin:'12px 0 12px' }} />

      {/* Bobot scoring */}
      <div>
        <div style={{ fontSize:11, fontWeight:600, color:'var(--khas-muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>
          Bobot penilaian
        </div>
        {[
          ['Jumlah tenant',     '20%'],
          ['Sertifikasi Halal', '25%'],
          ['Aman & Sehat',      '20%'],
          ['Fasilitas ibadah',  '12%'],
          ['Pengelolaan',       '11%'],
          ['Sarana prasarana',  '12%'],
        ].map(([label, bobot]) => (
          <div key={label} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'3px 0', color:'var(--khas-muted)' }}>
            <span>{label}</span>
            <span style={{ fontWeight:600, color:'var(--khas-ink-2)' }}>{bobot}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ScorePanel